import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { FaArrowLeft, FaSave, FaUser, FaBook, FaClipboardList } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { createStudentReport, clearError, clearSuccessMessage } from '../../redux/slices/studentReportsSlice';
import { getTeacherSubjects } from '../../redux/slices/subjectsSlice';
import styles from './CreateReport.module.css';

function CreateReport() {
  const { isDarkMode } = useDarkMode();
  const { studentId, classId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { creatingReport, error, successMessage } = useSelector((state) => state.studentReports);
  const { currentUser } = useSelector((state) => state.auth);
  const { teacherSubjects, subjectsLoading } = useSelector((state) => state.subjects);
  
  const [formData, setFormData] = useState({
    report_name: '',
    absence: false,
    homework: false,
    weeknesses: false,
    test_degree: '',
    teacher_notes: '',
    student_id: parseInt(studentId),
    subject_id: ''
  });

  const [selectedSubject, setSelectedSubject] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // جلب المواد الدراسية للمعلم
    if (currentUser && currentUser.role === 'teacher') {
      dispatch(getTeacherSubjects());
    }
  }, [dispatch, currentUser]);

  useEffect(() => {
    // تعيين أول مادة دراسية متاحة عند تحميل المواد
    if (teacherSubjects && teacherSubjects.length > 0 && !selectedSubject) {
      const firstSubject = teacherSubjects[0];
      setSelectedSubject(firstSubject.id);
      setFormData(prev => ({
        ...prev,
        subject_id: firstSubject.id
      }));
    }
  }, [teacherSubjects, selectedSubject]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
    
    if (successMessage) {
      toast.success(successMessage);
      dispatch(clearSuccessMessage());
      // العودة لصفحة التقارير بعد النجاح
      setTimeout(() => {
        navigate(`/student-reports/${studentId}/${classId}`);
      }, 1500);
    }
  }, [error, successMessage, dispatch, navigate, studentId, classId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubjectChange = (e) => {
    const subjectId = parseInt(e.target.value);
    setSelectedSubject(subjectId);
    setFormData(prev => ({
      ...prev,
      subject_id: subjectId
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // التحقق من صحة البيانات
    if (!formData.report_name.trim()) {
      toast.error('يرجى إدخال اسم التقرير');
      return;
    }
    
    if (!formData.teacher_notes.trim()) {
      toast.error('يرجى إدخال ملاحظات المعلم');
      return;
    }
    
    if (!formData.subject_id) {
      toast.error('يرجى اختيار المادة الدراسية');
      return;
    }
    
    if (formData.test_degree && (isNaN(formData.test_degree) || formData.test_degree < 0 || formData.test_degree > 100)) {
      toast.error('درجة الاختبار يجب أن تكون بين 0 و 100');
      return;
    }
    
    // إرسال التقرير
    try {
      await dispatch(createStudentReport(formData)).unwrap();
    } catch (error) {
      console.error('Error creating report:', error);
    }
  };

  const handleBack = () => {
    navigate(`/student-reports/${studentId}/${classId}`);
  };

  // عرض loading إذا كانت المواد الدراسية قيد التحميل
  if (subjectsLoading) {
    return (
      <div className={`${styles.createReportPage} ${isDarkMode ? styles.darkMode : ''}`}>
        <div className={styles.container}>
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>جاري تحميل المواد الدراسية...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.createReportPage} ${isDarkMode ? styles.darkMode : ''}`}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleBack}>
            <FaArrowLeft />
            رجوع
          </button>
          
          <div className={styles.titleSection}>
            <h1>إنشاء تقرير جديد</h1>
            <p>إضافة تقرير جديد للطالب</p>
          </div>
        </div>

        {/* Form */}
        <div className={styles.formContainer}>
          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Report Name */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <FaClipboardList />
                اسم التقرير *
              </label>
              <input
                type="text"
                name="report_name"
                value={formData.report_name}
                onChange={handleInputChange}
                placeholder="أدخل اسم التقرير..."
                className={styles.input}
                required
              />
            </div>

            {/* Subject Selection */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <FaBook />
                المادة الدراسية *
              </label>
              <select
                value={selectedSubject}
                onChange={handleSubjectChange}
                className={styles.select}
                required
              >
                <option value="">اختر المادة الدراسية</option>
                {teacherSubjects && teacherSubjects.map(subject => (
                  <option key={subject.id} value={subject.id}>
                    {subject.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Report Types */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                نوع التقرير
              </label>
              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="absence"
                    checked={formData.absence}
                    onChange={handleInputChange}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>تقرير غياب</span>
                </label>
                
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="homework"
                    checked={formData.homework}
                    onChange={handleInputChange}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>تقرير واجبات</span>
                </label>
                
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="weeknesses"
                    checked={formData.weeknesses}
                    onChange={handleInputChange}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>تقرير نقاط ضعف</span>
                </label>
              </div>
            </div>

            {/* Test Score */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                درجة الاختبار (اختياري)
              </label>
              <input
                type="number"
                name="test_degree"
                value={formData.test_degree}
                onChange={handleInputChange}
                placeholder="أدخل درجة الاختبار (0-100)"
                min="0"
                max="100"
                className={styles.input}
              />
            </div>

            {/* Teacher Notes */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                ملاحظات المعلم *
              </label>
              <textarea
                name="teacher_notes"
                value={formData.teacher_notes}
                onChange={handleInputChange}
                placeholder="أدخل ملاحظاتك حول الطالب..."
                rows="6"
                className={styles.textarea}
                required
              />
            </div>

            {/* Submit Button */}
            <div className={styles.formActions}>
              <button
                type="button"
                onClick={handleBack}
                className={styles.cancelButton}
                disabled={creatingReport}
              >
                إلغاء
              </button>
              
              <button
                type="submit"
                className={styles.submitButton}
                disabled={creatingReport}
              >
                {creatingReport ? (
                  <>
                    <div className={styles.spinner}></div>
                    جاري الإنشاء...
                  </>
                ) : (
                  <>
                    <FaSave />
                    إنشاء التقرير
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateReport;
